import React, { useState } from 'react';
import { usePDFDocuments } from '../../hooks/usePDFDocuments';
import PDFModal from '../PDFModal';
import './PDFDocumentManagement.css';

const PDFDocumentManagement = () => {
  const { 
    documents, 
    isLoading, 
    updateDocument, 
    deleteDocument, 
    addDocument, 
    getDocumentUrl,
    resetToDefaults 
  } = usePDFDocuments();

  const [modalState, setModalState] = useState({
    isOpen: false,
    pdfUrl: '',
    title: ''
  });

  const [editingDoc, setEditingDoc] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDocForm, setNewDocForm] = useState({
    name: '',
    icon: 'fas fa-file-pdf',
    filename: '',
    category: 'SVG'
  });

  const iconOptions = [
    'fas fa-file-alt',
    'fas fa-shield-alt',
    'fas fa-search',
    'fas fa-cogs',
    'fas fa-exclamation-triangle',
    'fas fa-exchange-alt',
    'fas fa-file-contract',
    'fas fa-file-pdf',
    'fas fa-document',
    'fas fa-clipboard',
    'fas fa-certificate',
    'fas fa-balance-scale'
  ];

  const openPDFModal = (document) => {
    setModalState({
      isOpen: true,
      pdfUrl: getDocumentUrl(document),
      title: document.name
    });
  };

  const closePDFModal = () => {
    setModalState({
      isOpen: false,
      pdfUrl: '',
      title: ''
    });
  };

  const handleEdit = (document) => {
    setEditingDoc({ ...document });
  };

  const handleSaveEdit = () => {
    if (editingDoc) {
      updateDocument(editingDoc);
      setEditingDoc(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingDoc(null);
  };

  const handleDelete = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      deleteDocument(documentId);
    }
  };

  const handleAddDocument = () => {
    if (newDocForm.name && newDocForm.filename) {
      addDocument(newDocForm);
      setNewDocForm({
        name: '',
        icon: 'fas fa-file-pdf',
        filename: '',
        category: 'SVG'
      });
      setShowAddForm(false);
    }
  };

  const handleFileUpload = (event, docId = null) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      // In a real application, you would upload this to your server
      // For now, we'll just update the filename
      const filename = file.name;
      
      if (docId) {
        // Updating existing document
        const document = documents.find(doc => doc.id === docId);
        if (document) {
          updateDocument({ ...document, filename });
        }
      } else {
        // Adding to new document form
        setNewDocForm(prev => ({ ...prev, filename }));
      }
    } else {
      alert('Please select a PDF file.');
    }
  };

  return (
    <div className="pdf-document-management">
      <div className="pdf-management-header">
        <h2>PDF Document Management</h2>
        <p>Manage regulatory documents and client agreements</p>
        
        <div className="pdf-management-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <i className="fas fa-plus"></i>
            Add New Document
          </button>
          <button 
            className="btn btn-secondary"
            onClick={resetToDefaults}
            title="Reset to default documents"
          >
            <i className="fas fa-undo"></i>
            Reset Defaults
          </button>
        </div>
      </div>

      {/* Add New Document Form */}
      {showAddForm && (
        <div className="add-document-form">
          <h3>Add New Document</h3>
          
          <div className="form-group">
            <label>Document Name</label>
            <input
              type="text"
              value={newDocForm.name}
              onChange={(e) => setNewDocForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Privacy Policy"
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={newDocForm.category}
              onChange={(e) => setNewDocForm(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="SVG">SVG</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Icon</label>
            <select
              value={newDocForm.icon}
              onChange={(e) => setNewDocForm(prev => ({ ...prev, icon: e.target.value }))}
            >
              {iconOptions.map(icon => (
                <option key={icon} value={icon}>
                  {icon.replace('fas fa-', '').replace('-', ' ')}
                </option>
              ))}
            </select>
            <div className="icon-preview">
              <i className={newDocForm.icon}></i>
            </div>
          </div>

          <div className="form-group">
            <label>Upload PDF</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => handleFileUpload(e)}
            />
            {newDocForm.filename && (
              <div className="filename-display">
                <i className="fas fa-file-pdf"></i>
                {newDocForm.filename}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button 
              className="btn btn-primary"
              onClick={handleAddDocument}
              disabled={!newDocForm.name || !newDocForm.filename}
            >
              Add Document
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="pdf-documents-list">
        {isLoading && (
          <div className="loading-indicator">
            <i className="fas fa-spinner fa-spin"></i>
            Updating documents...
          </div>
        )}

        {documents.map(document => (
          <div key={document.id} className="pdf-document-card">
            {editingDoc && editingDoc.id === document.id ? (
              /* Edit Mode */
              <div className="document-edit-form">
                <div className="form-group">
                  <label>Document Name</label>
                  <input
                    type="text"
                    value={editingDoc.name}
                    onChange={(e) => setEditingDoc(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>Icon</label>
                  <select
                    value={editingDoc.icon}
                    onChange={(e) => setEditingDoc(prev => ({ ...prev, icon: e.target.value }))}
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>
                        {icon.replace('fas fa-', '').replace('-', ' ')}
                      </option>
                    ))}
                  </select>
                  <div className="icon-preview">
                    <i className={editingDoc.icon}></i>
                  </div>
                </div>

                <div className="form-group">
                  <label>Replace PDF</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, document.id)}
                  />
                  <div className="current-filename">
                    Current: {editingDoc.filename}
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-primary btn-sm" onClick={handleSaveEdit}>
                    <i className="fas fa-save"></i> Save
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div className="document-info">
                  <div className="document-icon">
                    <i className={document.icon}></i>
                  </div>
                  <div className="document-details">
                    <h4>{document.name}</h4>
                    <p className="document-filename">{document.filename}</p>
                    <span className="document-category">{document.category}</span>
                  </div>
                </div>

                <div className="document-actions">
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => openPDFModal(document)}
                    title="Preview document"
                  >
                    <i className="fas fa-eye"></i>
                  </button>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEdit(document)}
                    title="Edit document"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button 
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(document.id)}
                    title="Delete document"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {documents.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-file-pdf"></i>
            <h3>No documents uploaded</h3>
            <p>Add your first PDF document to get started.</p>
          </div>
        )}
      </div>

      {/* PDF Modal */}
      <PDFModal 
        isOpen={modalState.isOpen}
        onClose={closePDFModal}
        pdfUrl={modalState.pdfUrl}
        title={modalState.title}
      />
    </div>
  );
};

export default PDFDocumentManagement;